import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import useTrackTabChange from 'hooks/general/useTrackTabChange';

if (typeof window !== 'undefined') {
    var G2 = require('@antv/g2');
    var { Chart } = G2;
}
interface IProps {
    dataset: Array<any>;
    chartContainer: string;
    padding?: [number, number, number, number];
    height: string | number;
    fields?: Array<string>;
    barWidth?: string;
    legendList?: Array<any>;
    labelFill?: string;
    colorList?: Array<any>;
    loading?: boolean;
    error?: any;
    additionalStyle?: any;
    enableDataClick?: boolean;
    dataClickHandler?: (event: any) => void;
    hideLegend?: boolean;
    minBound?: number;
    maxBound?: number;
}

const GroupedBarChart: FC<IProps> = (props: IProps) => {
    const {
        dataset,
        chartContainer,
        height,
        loading,
        error,
        fields = ['category', 'type', 'value', 'categoryAttribute', 'categoryType', 'total'],
        barWidth = '40',
        legendList = [
            { label: 'Own Elasticity' },
            { label: 'Internal Competitor Elasticity' },
            { label: 'External Competitor Elasticity' },
            { label: 'Total Elasticity' },
        ],

        colorList = ['#69D4E8', '#E469B2', '#EDB44E'],
        additionalStyle,
        enableDataClick,
        dataClickHandler = () => {},
        hideLegend,
        minBound,
        maxBound,
        padding = [0, 40, 0, 10],
    } = props;
    const isWindowTabActive = useTrackTabChange();

    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphLabelColorAlt, graphFontFamily } = commonSetting;

    if (!loading && error) {
        return <div>Error occurred </div>;
    }

    useEffect(() => {
        if (!window) return;
        const chart = new Chart({
            container: chartContainer,
            autoFit: true,
            height: height,
            padding: 'auto',
            appendPadding: padding,
        });
        chart.data(dataset);
        chart.scale(fields[2], {
            nice: true,
            min: minBound,
            max: maxBound,
        });

        // Hide Y-axis (or X-axis if transposed) scale information
        chart.axis(fields[0], {
            title: null,
            label: null, // Hide labels
            line: null, // Hide axis line
            tickLine: null, // Hide tick marks
        });

        // Hide X-axis (or Y-axis if transposed) scale information
        chart.axis(fields[2], {
            label: null, // Hide labels
            line: null, // Hide axis line
            tickLine: null, // Hide tick marks
            grid: {
                line: {
                    style: {
                        lineDash: [3, 3],
                    },
                },
            },
        });

        if (hideLegend) {
            chart.legend(false);
        }

        chart.coordinate('rect').transpose().reflect('x');
        chart.tooltip({
            showMarkers: false,
            shared: false,
            showCrosshairs: false,
            follow: true,
            showNil: false,
        });

        if (enableDataClick) {
            chart.on('element:click', (...args: any) => {
                dataClickHandler(args);
            });
        }

        chart
            .interval()
            .adjust('stack')
            .position(`${fields[0]}*${fields[2]}`)
            .color(`${fields[1]}*${fields[0]}`, (type: any, category: any) => {
                if (type && type.includes(legendList[0].label)) {
                    return colorList[0];
                }
                if (type && type.includes(legendList[1].label)) {
                    return colorList[1];
                }
                if (type && type.includes(legendList[2].label)) {
                    return colorList[2];
                }
                if (type && type.includes(legendList[3].label)) {
                    return 'transparent';
                }
                if (type === null) {
                    return 'transparent';
                }
            })
            .size(barWidth)
            .label(`${fields[2]}*${fields[1]}*${fields[0]}`, (val: any, t: any, category: any) => {
                // hide label for lesser values
                if (Math.abs(val) < 0.1) return;

                return {
                    position: t !== 'Total Elasticity' ? 'middle' : 'left',
                    content: val,
                    offsetX: t === 'Total Elasticity' ? 20 : 0,
                    style: {
                        fontSize: 13,
                        fill: graphLabelColorAlt,
                        fontFamily: graphFontFamily,
                    },
                };
            })
            .style({
                stroke: 'white',
                lineWidth: 2,
            });

        chart.tooltip({
            shared: true,
        });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, isWindowTabActive]);
    return (
        <div className="flex-col">
            <div
                id={chartContainer}
                style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }}
                className={`w-full mt-5`}
            />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    return _.isEqual(prevProps, nextProps);
}

export default React.memo(GroupedBarChart, arePropsEqual);
